import React, { useState, useEffect, useMemo } from 'react';
import { X, Upload, Plus, Trash2 } from 'lucide-react';
import {
  fetchProduct,
  fetchSubcategories,
  fetchColors,
  fetchSizes,
  uploadImages as uploadImagesApi,
} from '../../services/productApi';

const ProductForm = ({ product, mode = 'edit', onSubmit, onCancel }) => {
  const normalizeSize = (s) => {
    const v = String(s || '').toUpperCase();
    if (v === 'XXL') return '2XL';
    if (v === 'XXXL') return '3XL';
    return v;
  };
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    subcategory: '',
    price: '',
    cost: '',
    sku: '',
    stock: '',
    minStock: '',
    status: 'active',
    tags: '',
    images: [],
    colors: [],
    sizes: [],
    weight: '',
    dimensions: {
      length: '',
      width: '',
      height: '',
    },
    material: '',
    brand: '',
    warranty: '',
    features: [],
    specifications: [],
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [subcategories, setSubcategories] = useState([]);
  const [availableColors, setAvailableColors] = useState([]);
  const [availableSizes, setAvailableSizes] = useState([]);
  const [newFeature, setNewFeature] = useState('');
  const [newSpecValue, setNewSpecValue] = useState('');
  const [uploadingImages, setUploadingImages] = useState(false);
  console.log(uploadingImages);
  const categories = ['Paints', 'Workwear'];


  // Standardized Color Map matching ProductDetails
  const COLOR_MAP = {
    Black: '#000000',
    Grey: '#9CA3AF',
    Gray: '#9CA3AF',
    White: '#FFFFFF',
    Yellow: '#FDE047',
    Blue: '#1D4ED8',
    Green: '#16A34A',
    'Mint Green': '#86EFAC',
    Brown: '#92400E',
    Pink: '#F472B6',
    Red: '#EF4444',
    'Bi-Color':
      'linear-gradient(135deg, #ff0000, #ffa500, #ffff00, #00ff00, #0000ff, #4b0082, #ee82ee)',
    Champagne: '#F5DEB3',
    Silver: '#D1D5DB',
    Purple: '#6B21A8',
    Navy: '#1E3A8A',
    Orange: '#F97316',
    Beige: '#F5F5DC',
  };

  const AVAILABLE_COLORS = Object.keys(COLOR_MAP);

  // Fallback subcategories if API returns empty
  const WORKWEAR_SUBCATS_FALLBACK = useMemo(
    () => ['Polo Shirts', 'Jackets', 'Shoes', 'Pants', 'T-shirts', 'Shorts'],
    []
  );
  const PAINTS_SUBCATS_FALLBACK = useMemo(
    () => [
      'Mastics / Putties',
      'Degreasers',
      'Abrasives',
      'Fillers / Apprêts',
      'Ready Mix / Finished Paint',
      'Masking Products',
      'Primers',
      'Stone Chip Protectors',
      'Miscellaneous / Accessories',
      'Varnishes / Clearcoats',
      'Sealants',
      'Paint Equipment / Tools',
      'Thinners / Reducers',
      'Spray Products / Aerosols',
      'Base Coats (Color Mixing System)',
    ],
    []
  );

  const statuses = [
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
    { value: 'out_of_stock', label: 'Out of Stock' },
    { value: 'discontinued', label: 'Discontinued' },
  ];

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const sizesData = await fetchSizes();
        // Colors are now static from COLOR_MAP, so we don't fetch them
        setAvailableSizes(sizesData);
      } catch (error) {
        console.error('Error loading initial data:', error);
      }
    };
    loadInitialData();
  }, []);

  useEffect(() => {
    if (product && product.id) {
      // Load product data if we have an ID
      const loadProduct = async () => {
        setLoading(true);
        try {
          const productData = await fetchProduct(product.id);
          setFormData({
            name: productData.name || '',
            description: productData.description || '',
            // Translations
            name_de: productData.translations?.name?.de || '',
            description_de: productData.translations?.description?.de || '',
            name_fr: productData.translations?.name?.fr || '',
            description_fr: productData.translations?.description?.fr || '',
            name_ru: productData.translations?.name?.ru || '',
            description_ru: productData.translations?.description?.ru || '',
            // Localized Features & Specs
            features_de: productData.translations?.features?.de || [],
            features_fr: productData.translations?.features?.fr || [],
            features_ru: productData.translations?.features?.ru || [],
            specifications_de: productData.translations?.specifications?.de || [],
            specifications_fr: productData.translations?.specifications?.fr || [],
            specifications_ru: productData.translations?.specifications?.ru || [],
            //
            category: productData.category || '',
            subcategory: productData.subcategory || '',
            price: productData.price?.toString() || '',
            cost: productData.cost?.toString() || '',
            sku: productData.sku || '',
            stock: productData.stock?.toString() || '',
            minStock: productData.minStock?.toString() || '',
            status: productData.status || 'active',
            tags: productData.tags?.join(', ') || '',
            images: productData.images || [],
            // Prefer workwear nested fields, fallback to root for older data
            colors: productData.workwear?.colors || productData.colors || [],
            sizes: Array.isArray(productData.workwear?.sizes || productData.sizes)
              ? (productData.workwear?.sizes || productData.sizes).map(normalizeSize)
              : [],
            weight: productData.weight || '',
            dimensions: productData.dimensions || {
              length: '',
              width: '',
              height: '',
            },
            material: productData.material || '',
            brand: productData.brand || '',
            warranty: productData.warranty || '',
            warranty: productData.warranty || '',
            features: productData.features || [],
            specifications: productData.specifications || [],
            // Load localized features
            features_de: productData.translations?.features?.de || [],
            features_fr: productData.translations?.features?.fr || [],
            features_ru: productData.translations?.features?.ru || [],
            // Load localized specifications
            specifications_de: productData.translations?.specifications?.de || [],
            specifications_fr: productData.translations?.specifications?.fr || [],
            specifications_ru: productData.translations?.specifications?.ru || [],
          });
        } catch (error) {
          console.error('Error loading product:', error);
        } finally {
          setLoading(false);
        }
      };
      loadProduct();
    } else if (product) {
      // Use provided product data directly
      setFormData({
        name: product.name || '',
        description: product.description || '',
        // Load translations
        name_de: product.translations?.name?.de || '',
        description_de: product.translations?.description?.de || '',
        name_fr: product.translations?.name?.fr || '',
        description_fr: product.translations?.description?.fr || '',
        name_ru: product.translations?.name?.ru || '',
        description_ru: product.translations?.description?.ru || '',
        //
        category: product.category || '',
        subcategory: product.subcategory || '',
        price: product.price?.toString() || '',
        cost: product.cost?.toString() || '',
        sku: product.sku || '',
        stock: product.stock?.toString() || '',
        minStock: product.minStock?.toString() || '',
        status: product.status || 'active',
        tags: product.tags?.join(', ') || '',
        images: product.images || [],
        // Prefer workwear nested fields
        colors: product.workwear?.colors || product.colors || [],
        sizes: Array.isArray(product.workwear?.sizes || product.sizes)
          ? (product.workwear?.sizes || product.sizes).map(normalizeSize)
          : [],
        weight: product.weight || '',
        dimensions: product.dimensions || { length: '', width: '', height: '' },
        material: product.material || '',
        brand: product.brand || '',
        warranty: product.warranty || '',
        features: product.features || [],
        specifications: product.specifications || {},
      });
    }
  }, [product]);

  // Load subcategories when category changes
  useEffect(() => {
    const loadSubcategories = async () => {
      if (formData.category) {
        try {
          const subcats = await fetchSubcategories(formData.category);
          const normalized = Array.isArray(subcats)
            ? subcats.map((s) => String(s))
            : [];
          const onlyMisc =
            normalized.length === 1 &&
            normalized[0].trim().toLowerCase() === 'miscellaneous';

          // Prefer curated list for Paints; merge in any extra API values (excluding 'miscellaneous')
          if (formData.category === 'Paints') {
            const extras = normalized
              .filter(Boolean)
              .filter((s) => s.trim().toLowerCase() !== 'miscellaneous')
              .filter((s) => !PAINTS_SUBCATS_FALLBACK.includes(s));
            setSubcategories([...PAINTS_SUBCATS_FALLBACK, ...extras]);
          } else if (
            normalized.length > 1 ||
            (normalized.length === 1 && !onlyMisc)
          ) {
            setSubcategories(normalized);
          } else {
            // Fallback to static lists when backend has no data yet
            if (formData.category === 'Workwear') {
              setSubcategories(WORKWEAR_SUBCATS_FALLBACK);
            } else if (formData.category === 'Paints') {
              setSubcategories(PAINTS_SUBCATS_FALLBACK);
            } else {
              setSubcategories([]);
            }
          }
          // Reset subcategory when category changes
          setFormData((prev) => ({ ...prev, subcategory: '' }));
        } catch (error) {
          console.error('Error loading subcategories:', error);
          // On error, also fallback
          if (formData.category === 'Workwear') {
            setSubcategories(WORKWEAR_SUBCATS_FALLBACK);
          } else if (formData.category === 'Paints') {
            setSubcategories(PAINTS_SUBCATS_FALLBACK);
          } else {
            setSubcategories([]);
          }
        }
      } else {
        setSubcategories([]);
      }
    };
    loadSubcategories();
  }, [formData.category, PAINTS_SUBCATS_FALLBACK, WORKWEAR_SUBCATS_FALLBACK]);

  // Load sizes when subcategory changes
  useEffect(() => {
    const loadSizes = async () => {
      if (formData.subcategory) {
        try {
          const sizesData = await fetchSizes(formData.subcategory);
          setAvailableSizes(sizesData);
        } catch (error) {
          console.error('Error loading sizes:', error);
        }
      }
    };
    loadSizes();
  }, [formData.subcategory]);

  const handleChange = (e) => {
    // Don't allow changes in view mode
    if (mode === 'view') return;

    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const handleDimensionChange = (dimension, value) => {
    if (mode === 'view') return;
    setFormData((prev) => ({
      ...prev,
      dimensions: {
        ...prev.dimensions,
        [dimension]: value,
      },
    }));
  };

  const handleColorToggle = (color) => {
    if (mode === 'view') return;
    setFormData((prev) => ({
      ...prev,
      colors: prev.colors.includes(color)
        ? prev.colors.filter((c) => c !== color)
        : [...prev.colors, color],
    }));
  };

  const handleSizeToggle = (size) => {
    if (mode === 'view') return;
    setFormData((prev) => ({
      ...prev,
      sizes: prev.sizes.includes(size)
        ? prev.sizes.filter((s) => s !== size)
        : [...prev.sizes, size],
    }));
  };

  const handleAddFeature = () => {
    if (mode === 'view' || !newFeature.trim()) return;

    setFormData((prev) => ({
      ...prev,
      features: [...(prev.features || []), newFeature.trim()],
    }));
    setNewFeature('');
  };

  const handleRemoveFeature = (index) => {
    if (mode === 'view') return;

    setFormData((prev) => ({
      ...prev,
      features: (prev.features || []).filter((_, i) => i !== index),
    }));
  };

  const handleAddSpecification = () => {
    if (mode === 'view' || !newSpecValue.trim()) return;

    setFormData((prev) => ({
      ...prev,
      specifications: [...(prev.specifications || []), newSpecValue.trim()],
    }));
    setNewSpecValue('');
  };

  const handleRemoveSpecification = (index) => {
    if (mode === 'view') return;

    setFormData((prev) => ({
      ...prev,
      specifications: (prev.specifications || []).filter((_, i) => i !== index),
    }));
  };

  const handleImageUpload = async (e) => {
    // Don't allow image upload in view mode
    if (mode === 'view') return;

    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploadingImages(true);
    try {
      const urls = await uploadImagesApi(files);
      setFormData((prev) => ({
        ...prev,
        images: [...(prev.images || []), ...urls],
      }));
    } catch (err) {
      console.error('Image upload failed:', err);
    } finally {
      setUploadingImages(false);
    }
  };

  const removeImage = (index) => {
    // Don't allow image removal in view mode
    if (mode === 'view') return;

    setFormData((prev) => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index),
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Product name is required';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }

    if (!formData.category) {
      newErrors.category = 'Category is required';
    }

    if (formData.category === 'Workwear' && !formData.subcategory) {
      newErrors.subcategory = 'Subcategory is required for workwear';
    }

    if (
      !formData.price ||
      isNaN(parseFloat(formData.price)) ||
      parseFloat(formData.price) <= 0
    ) {
      newErrors.price = 'Valid price is required';
    }

    if (
      !formData.cost ||
      isNaN(parseFloat(formData.cost)) ||
      parseFloat(formData.cost) < 0
    ) {
      newErrors.cost = 'Valid cost is required';
    }

    if (!formData.sku.trim()) {
      newErrors.sku = 'SKU is required';
    }

    if (
      !formData.stock ||
      isNaN(parseInt(formData.stock)) ||
      parseInt(formData.stock) < 0
    ) {
      newErrors.stock = 'Valid stock quantity is required';
    }

    if (
      !formData.minStock ||
      isNaN(parseInt(formData.minStock)) ||
      parseInt(formData.minStock) < 0
    ) {
      newErrors.minStock = 'Valid minimum stock is required';
    }

    if (formData.category === 'Workwear' && formData.colors.length === 0) {
      newErrors.colors = 'At least one color is required for workwear';
    }

    if (formData.category === 'Workwear' && formData.sizes.length === 0) {
      newErrors.sizes = 'At least one size is required for workwear';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAutofill = () => {
    setFormData({
      ...formData,
      name: 'Test Product ' + Math.floor(Math.random() * 1000),
      description: 'This is a test description.',
      category: 'Workwear',
      subcategory: 'Pants',
      price: '50',
      cost: '20',
      sku: 'TEST-' + Math.floor(Math.random() * 10000),
      stock: '100',
      minStock: '10',
      weight: '1.5',
      colors: ['Black', 'Blue'],
      sizes: ['M', 'L'],
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const submitData = {
        ...formData,
        price: parseFloat(formData.price),
        cost: parseFloat(formData.cost),
        stock: parseInt(formData.stock),
        minStock: parseInt(formData.minStock),
        tags: formData.tags
          .split(',')
          .map((tag) => tag.trim())
          .map((tag) => tag.trim())
          .filter((tag) => tag),
      };

      // Correctly Structure for Mongoose 'workwear' sub-document
      if (formData.category === 'Workwear') {
        submitData.workwear = {
          colors: formData.colors,
          sizes: formData.sizes,
          brand: formData.brand,
          material: formData.material,
        };
      }

      console.log('ProductForm submitting:', submitData);

      await onSubmit(submitData);
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg p-8 text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
        <p className="mt-2 text-gray-600">Loading product...</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="flex justify-end">
        <button
          type="button"
          onClick={handleAutofill}
          className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors"
        >
          Autofill Test Data
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">


        {/* Product Name */}
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Product Name *
          </label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            readOnly={mode === 'view'}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${errors.name
              ? 'border-red-500'
              : 'border-gray-300'
              } ${mode === 'view' ? 'bg-gray-50 cursor-not-allowed' : ''}`}
            placeholder="Enter product name"
          />
          {errors.name && (
            <p className="mt-1 text-sm text-red-600">{errors.name}</p>
          )}
        </div>

        {/* Description */}
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Description *
          </label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            rows={3}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${errors.description
              ? 'border-red-500'
              : 'border-gray-300'
              }`}
            placeholder="Enter product description"
          />
          {errors.description && (
            <p className="mt-1 text-sm text-red-600">{errors.description}</p>
          )}
        </div>

        {/* Category */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Category *
          </label>
          <select
            name="category"
            value={formData.category}
            onChange={handleChange}
            readOnly={mode === 'view'}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${errors.category ? 'border-red-500' : 'border-gray-300'
              } ${mode === 'view' ? 'bg-gray-50 cursor-not-allowed' : ''}`}
          >
            <option value="">Select category</option>
            {categories.map((category) => (
              <option key={category} value={category}>
                {category}
              </option>
            ))}
          </select>
          {errors.category && (
            <p className="mt-1 text-sm text-red-600">{errors.category}</p>
          )}
        </div>

        {/* Subcategory - Show when a category is selected (required only for Workwear) */}
        {formData.category && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {formData.category === 'Workwear'
                ? 'Subcategory *'
                : 'Subcategory'}
            </label>
            <select
              name="subcategory"
              value={formData.subcategory}
              onChange={handleChange}
              readOnly={mode === 'view'}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${errors.subcategory ? 'border-red-500' : 'border-gray-300'
                } ${mode === 'view' ? 'bg-gray-50 cursor-not-allowed' : ''}`}
            >
              <option value="">Select subcategory</option>
              {subcategories.map((subcategory) => (
                <option key={subcategory} value={subcategory}>
                  {subcategory}
                </option>
              ))}
            </select>
            {errors.subcategory && (
              <p className="mt-1 text-sm text-red-600">{errors.subcategory}</p>
            )}
          </div>
        )}

        {/* Status */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Status
          </label>
          <select
            name="status"
            value={formData.status}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            {statuses.map((status) => (
              <option key={status.value} value={status.value}>
                {status.label}
              </option>
            ))}
          </select>
        </div>

        {/* Price */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Price *
          </label>
          <div className="relative">
            <span className="absolute left-3 top-2 text-gray-500">$</span>
            <input
              type="number"
              name="price"
              value={formData.price}
              onChange={handleChange}
              step="0.01"
              min="0"
              className={`w-full pl-8 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${errors.price ? 'border-red-500' : 'border-gray-300'
                }`}
              placeholder="0.00"
            />
          </div>
          {errors.price && (
            <p className="mt-1 text-sm text-red-600">{errors.price}</p>
          )}
        </div>

        {/* Cost */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Cost
          </label>
          <div className="relative">
            <span className="absolute left-3 top-2 text-gray-500">$</span>
            <input
              type="number"
              name="cost"
              value={formData.cost}
              onChange={handleChange}
              step="0.01"
              min="0"
              className={`w-full pl-8 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${errors.cost ? 'border-red-500' : 'border-gray-300'
                }`}
              placeholder="0.00"
            />
          </div>
          {errors.cost && (
            <p className="mt-1 text-sm text-red-600">{errors.cost}</p>
          )}
        </div>

        {/* SKU */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            SKU *
          </label>
          <input
            type="text"
            name="sku"
            value={formData.sku}
            onChange={handleChange}
            readOnly={mode === 'view'}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${errors.sku ? 'border-red-500' : 'border-gray-300'
              } ${mode === 'view' ? 'bg-gray-50 cursor-not-allowed' : ''}`}
            placeholder="Enter SKU"
          />
          {errors.sku && (
            <p className="mt-1 text-sm text-red-600">{errors.sku}</p>
          )}
        </div>

        {/* Colors - Only for Workwear */}
        {formData.category === 'Workwear' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Colors *
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
              {AVAILABLE_COLORS.map((color) => {
                const isSelected = formData.colors.includes(color);
                const colorValue = COLOR_MAP[color] || '#E5E7EB';
                const isGradient = colorValue.startsWith('linear-gradient');
                const swatchStyle = isGradient
                  ? { backgroundImage: colorValue }
                  : { backgroundColor: colorValue };

                return (
                  <label
                    key={color}
                    className={`flex flex-col items-center gap-2 px-3 py-2 border rounded-full cursor-pointer select-none transition-all ${isSelected
                      ? 'border-black ring-1 ring-black bg-gray-50'
                      : 'border-gray-300 hover:bg-gray-50'
                      } ${mode === 'view' ? 'cursor-not-allowed opacity-80' : ''
                      }`}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handleColorToggle(color)}
                      disabled={mode === 'view'}
                      className="hidden"
                    />
                    <span
                      className="inline-block w-8 h-8 rounded-full border border-gray-200 shadow-sm"
                      style={swatchStyle}
                      title={color}
                    />
                    {/* <span className="text-xs text-gray-600 font-medium">{color}</span> */}
                  </label>
                );
              })}
            </div>
            {errors.colors && (
              <p className="mt-1 text-sm text-red-600">{errors.colors}</p>
            )}
          </div>
        )}

        {/* Sizes - Only for Workwear */}
        {formData.category === 'Workwear' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Sizes *
            </label>
            <div className="grid grid-cols-4 gap-2">
              {availableSizes.map((size) => (
                <label
                  key={size}
                  className={`flex items-center space-x-2 p-2 border rounded-lg cursor-pointer ${formData.sizes.includes(size)
                    ? 'bg-blue-50 border-blue-500'
                    : 'border-gray-300 hover:bg-gray-50'
                    } ${mode === 'view' ? 'cursor-not-allowed' : ''}`}
                >
                  <input
                    type="checkbox"
                    checked={formData.sizes.includes(size)}
                    onChange={() => handleSizeToggle(size)}
                    disabled={mode === 'view'}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm">{size}</span>
                </label>
              ))}
            </div>
            {errors.sizes && (
              <p className="mt-1 text-sm text-red-600">{errors.sizes}</p>
            )}
          </div>
        )}

        {/* Stock */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Stock Quantity *
          </label>
          <input
            type="number"
            name="stock"
            value={formData.stock}
            onChange={handleChange}
            min="0"
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${errors.stock ? 'border-red-500' : 'border-gray-300'
              }`}
            placeholder="0"
          />
          {errors.stock && (
            <p className="mt-1 text-sm text-red-600">{errors.stock}</p>
          )}
        </div>

        {/* Min Stock */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Minimum Stock
          </label>
          <input
            type="number"
            name="minStock"
            value={formData.minStock}
            onChange={handleChange}
            min="0"
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${errors.minStock ? 'border-red-500' : 'border-gray-300'
              }`}
            placeholder="0"
          />
          {errors.minStock && (
            <p className="mt-1 text-sm text-red-600">{errors.minStock}</p>
          )}
        </div>

        {/* Brand */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Brand
          </label>
          <input
            type="text"
            name="brand"
            value={formData.brand}
            onChange={handleChange}
            readOnly={mode === 'view'}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${mode === 'view' ? 'bg-gray-50 cursor-not-allowed' : ''
              }`}
            placeholder="Enter brand name"
          />
        </div>

        {/* Material */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Material
          </label>
          <input
            type="text"
            name="material"
            value={formData.material}
            onChange={handleChange}
            readOnly={mode === 'view'}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${mode === 'view' ? 'bg-gray-50 cursor-not-allowed' : ''
              }`}
            placeholder="e.g., Cotton, Polyester, Leather"
          />
        </div>

        {/* Weight */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Weight (kg)
          </label>
          <input
            type="number"
            name="weight"
            value={formData.weight}
            onChange={handleChange}
            readOnly={mode === 'view'}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${mode === 'view' ? 'bg-gray-50 cursor-not-allowed' : ''
              }`}
            placeholder="0.0"
            step="0.1"
            min="0"
          />
        </div>

        {/* Warranty */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Warranty
          </label>
          <input
            type="text"
            name="warranty"
            value={formData.warranty}
            onChange={handleChange}
            readOnly={mode === 'view'}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${mode === 'view' ? 'bg-gray-50 cursor-not-allowed' : ''
              }`}
            placeholder="e.g., 1 year, 6 months"
          />
        </div>

        {/* Dimensions */}
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Dimensions (cm)
          </label>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Length</label>
              <input
                type="number"
                value={formData.dimensions.length}
                onChange={(e) =>
                  handleDimensionChange('length', e.target.value)
                }
                readOnly={mode === 'view'}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${mode === 'view' ? 'bg-gray-50 cursor-not-allowed' : ''
                  }`}
                placeholder="0"
                step="0.1"
                min="0"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Width</label>
              <input
                type="number"
                value={formData.dimensions.width}
                onChange={(e) => handleDimensionChange('width', e.target.value)}
                readOnly={mode === 'view'}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${mode === 'view' ? 'bg-gray-50 cursor-not-allowed' : ''
                  }`}
                placeholder="0"
                step="0.1"
                min="0"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Height</label>
              <input
                type="number"
                value={formData.dimensions.height}
                onChange={(e) =>
                  handleDimensionChange('height', e.target.value)
                }
                readOnly={mode === 'view'}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${mode === 'view' ? 'bg-gray-50 cursor-not-allowed' : ''
                  }`}
                placeholder="0"
                step="0.1"
                min="0"
              />
            </div>
          </div>
        </div>

        {/* Tags */}
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Tags
          </label>
          <div className="flex flex-wrap gap-2 mb-2">
            {formData.tags
              .split(',')
              .map((t) => t.trim())
              .filter(Boolean)
              .map((tag, idx) => (
                <span
                  key={idx}
                  className="inline-flex items-center gap-1 bg-gray-100 text-gray-800 px-2 py-1 rounded-full text-xs"
                >
                  {tag}
                  {mode !== 'view' && (
                    <button
                      type="button"
                      aria-label={`Remove tag ${tag}`}
                      onClick={() => {
                        const next = formData.tags
                          .split(',')
                          .map((t) => t.trim())
                          .filter(Boolean)
                          .filter((t) => t !== tag)
                          .join(', ');
                        setFormData((prev) => ({ ...prev, tags: next }));
                      }}
                      className="text-gray-500 hover:text-gray-700"
                    >
                      ×
                    </button>
                  )}
                </span>
              ))}
          </div>
          <input
            type="text"
            name="tags"
            value={formData.tags}
            onChange={(e) => {
              // normalize spaces around commas
              const v = e.target.value.replace(/\s*,\s*/g, ', ');
              handleChange({ target: { name: 'tags', value: v } });
            }}
            onKeyDown={(e) => {
              if (mode === 'view') return;
              if (e.key === 'Enter' || e.key === ',') {
                e.preventDefault();
                // ensure a trailing comma+space to finalize the tag
                const v = formData.tags.trim();
                const next = v.endsWith(',')
                  ? v + ' '
                  : v.length
                    ? v + ', '
                    : v;
                setFormData((prev) => ({ ...prev, tags: next }));
              }
            }}
            readOnly={mode === 'view'}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${mode === 'view' ? 'bg-gray-50 cursor-not-allowed' : ''
              }`}
            placeholder="Type a tag and press comma or Enter"
          />
        </div>

        {/* Images */}
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Product Images
          </label>
          <div className="space-y-4">
            {/* Image Upload */}
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center relative">
              {uploadingImages && (
                <div className="absolute inset-0 bg-white/80 z-10 flex flex-col items-center justify-center rounded-lg">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-2"></div>
                  <span className="text-sm text-blue-600 font-medium">Uploading images...</span>
                </div>
              )}
              <input
                type="file"
                id="image-upload"
                multiple
                accept="image/*"
                onChange={handleImageUpload}
                disabled={uploadingImages}
                className="hidden"
              />
              <label
                htmlFor="image-upload"
                className={`cursor-pointer flex flex-col items-center ${uploadingImages ? 'opacity-50' : ''}`}
              >
                <Upload className="h-8 w-8 text-gray-400 mb-2" />
                <span className="text-sm text-gray-600">
                  Click to upload images
                </span>
                <span className="text-xs text-gray-500">
                  PNG, JPG, GIF up to 10MB each
                </span>
              </label>
            </div>

            {/* Image Preview */}
            {formData.images.length > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {formData.images.map((image, index) => (
                  <div key={index} className="relative group">
                    <img
                      src={image}
                      alt={`Product ${index + 1}`}
                      className="w-full h-40 object-contain rounded-lg"
                    />
                    <button
                      type="button"
                      onClick={() => removeImage(index)}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 shadow"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Features */}
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Features
          </label>
          <div className="space-y-2">
            {(formData.features || []).map((feature, index) => (
              <div key={index} className="flex items-center space-x-2">
                <span className="text-sm text-gray-700 flex-1">{feature}</span>
                {mode !== 'view' && (
                  <button
                    type="button"
                    onClick={() => handleRemoveFeature(index)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))}
            {mode !== 'view' && (
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={newFeature}
                  onChange={(e) => setNewFeature(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Add a feature"
                  onKeyPress={(e) =>
                    e.key === 'Enter' &&
                    (e.preventDefault(), handleAddFeature())
                  }
                />
                <button
                  type="button"
                  onClick={handleAddFeature}
                  className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Specifications */}
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Specifications
          </label>
          <div className="space-y-2">
            {(formData.specifications || []).map((spec, index) => (
              <div key={index} className="flex items-center space-x-2">
                <span className="text-sm text-gray-600 flex-1">{spec}</span>
                {mode !== 'view' && (
                  <button
                    type="button"
                    onClick={() => handleRemoveSpecification(index)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))}
            {mode !== 'view' && (
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={newSpecValue}
                  onChange={(e) => setNewSpecValue(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Add a specification"
                  onKeyPress={(e) =>
                    e.key === 'Enter' &&
                    (e.preventDefault(), handleAddSpecification())
                  }
                />
                <button
                  type="button"
                  onClick={handleAddSpecification}
                  className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Form Actions */}
      <div className="flex items-center justify-end space-x-3 pt-6 border-t border-gray-200">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          {mode === 'view' ? 'Close' : 'Cancel'}
        </button>
        {mode !== 'view' && (
          <button
            type="submit"
            disabled={isSubmitting}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting
              ? 'Saving...'
              : mode === 'add'
                ? 'Create Product'
                : 'Update Product'}
          </button>
        )}
      </div>
    </form>
  );
};

export default ProductForm;
